import type { Placement } from '@popperjs/core';
import React, { useState } from 'react';
import overflowMenuIcon from '../icons/non-prefilled/16x16/overflowMenu.svg';
import TutorialBeacon from '../onboarding/tutorial/TutorialBeacon';
import type Button from './Button';
import IconButton, { NEGATIVE_SPACE } from './IconButton';
import Menu from './Menu';
import type { SVGIconSize } from './SVGIcon';
import SVGIcon from './SVGIcon';
import PopOver from './deprecated/PopOver';

type ButtonProps = React.ComponentProps<typeof Button>;

interface OverflowMenuButtonProps extends ButtonProps {
  iconSize: SVGIconSize;
  applyNegativeMargin: boolean;
}

const OverflowMenuButton = React.forwardRef<
  HTMLButtonElement,
  OverflowMenuButtonProps
>(
  (
    { iconSize, applyNegativeMargin, ...props }: OverflowMenuButtonProps,
    ref,
  ) => (
    <IconButton
      applyNegativeMargin={applyNegativeMargin}
      ref={ref}
      {...props}
      aria-label="More options"
    >
      <SVGIcon size={iconSize || 22}>{overflowMenuIcon}</SVGIcon>
    </IconButton>
  ),
);

interface OverflowMenuProps {
  // TODO: Once Popover is converted, change the props here to extend Popover's.
  beaconId?: string;
  beaconPlacement?: Placement;
  button?: React.ReactNode;
  children: React.ReactNode;
  iconSize?: SVGIconSize;
  onOpenChange?: (isOpen: boolean) => void;
  placement?: Placement;
  portal?: HTMLElement;
  applyNegativeMargin?: boolean;
  preventCloseOnContentClick?: boolean;
  modifiers?: React.ComponentProps<typeof PopOver>['modifiers'];
  ariaLabel?: string;
}

interface ControlledOverflowMenuProps extends OverflowMenuProps {
  open: boolean;
  onOpenChange: (isOpen: boolean) => void;
  inline?: boolean;
}

const ControlledOverflowMenu = ({
  beaconId,
  beaconPlacement,
  button,
  children,
  iconSize = 22,
  open,
  onOpenChange,
  portal = document.body,
  applyNegativeMargin = true,
  modifiers,
  ariaLabel,
  ...props
}: ControlledOverflowMenuProps) => (
  <PopOver
    display="inline-block"
    responsivePlacement="bottom"
    placement="bottom-end"
    popOver={<Menu ariaLabel={ariaLabel}>{children}</Menu>}
    popOverPortal={portal}
    open={open}
    onOpenChange={onOpenChange}
    trigger="click"
    modifiers={
      button
        ? modifiers
        : [
            applyNegativeMargin
              ? {
                  name: 'offset',
                  options: { offset: [NEGATIVE_SPACE, NEGATIVE_SPACE] },
                }
              : {},
            ...(modifiers ?? []),
          ]
    }
    {...props}
  >
    {button ||
      (beaconId ? (
        <TutorialBeacon id={beaconId} hide={open} placement={beaconPlacement}>
          {(beaconProps) => (
            <OverflowMenuButton
              active={open}
              iconSize={iconSize}
              applyNegativeMargin={applyNegativeMargin}
              {...beaconProps}
            />
          )}
        </TutorialBeacon>
      ) : (
        <OverflowMenuButton
          active={open}
          iconSize={iconSize}
          applyNegativeMargin={applyNegativeMargin}
        />
      ))}
  </PopOver>
);

interface UncontrolledOverflowMenuProps extends OverflowMenuProps {
  open?: undefined;
  onOpenChange?: undefined;
}

const UncontrolledOverflowMenu = (props: UncontrolledOverflowMenuProps) => {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const [open, setOpen] = useState(false);

  return (
    <ControlledOverflowMenu {...props} open={open} onOpenChange={setOpen} />
  );
};

const OverflowMenu = (
  props: ControlledOverflowMenuProps | UncontrolledOverflowMenuProps,
) =>
  props.onOpenChange ? (
    <ControlledOverflowMenu {...props} />
  ) : (
    <UncontrolledOverflowMenu {...props} />
  );

export default OverflowMenu;
